import React, { useContext, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { makeStyles, styled } from '@mui/styles';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Button from 'components/Button';
import { ConfigContext } from 'context/config';
import { snack } from 'utils/toast';
import { addProduct } from 'services/account.service';
import axios from 'axios';

const PRODUCTS = {
  qa: 'price_1JCVbzFOVxqXc60A7GthgzdI',
  prod: 'price_1JCVnmBoxEBjoujW0d2uv9GE',
};
const WEBHOOK = 'https://hooks.zapier.com/hooks/catch/465525/b3v3im8';

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiTypography-body1': {
      fontSize: 18,
    },
  },
  videoWrapper: {
    position: 'relative',
    boxShadow: '0 10px 6px -6px #777',
    backgroundColor: '#FFF',
    padding: 8,
    borderRadius: 5,
    border: '1px solid rgba(0,0,0,0.1)',
    borderBottom: '3px solid rgba(0,0,0,0.2)',
  },
  list: {
    'listStyleType': 'none',
    '& li': {
      'marginBottom': theme.spacing(2),
      '& .MuiSvgIcon-root': {
        paddingTop: 4,
      },
      '& .MuiTypography-body1': {
        fontSize: 22,
      },
    },
  },
  overlay: {
    'padding': 10,
    'position': 'absolute',
    'zIndex': 1,
    'cursor': 'pointer',
    'left': 0,
    'right': 0,
    'top': 0,
    'bottom': 0,
    'backgroundImage': "url('/assets/images/big-play-button.png')",
    'backgroundSize': '30%',
    'backgroundPosition': 'center',
    'backgroundRepeat': 'no-repeat',
    '& .unmute-button': {
      width: '100px',
      marginTop: 10,
      marginLeft: 10,
    },
  },
}));

const WeBelieve = ({ history }) => {
  const config = useContext(ConfigContext);
  const classes = useStyles();
  const user = useSelector((s) => s.user.user);
  const account = useSelector((s) => s.settings.account);
  const [playing, setPlaying] = useState(false);
  const productId = PRODUCTS[config.STAGE];

  useEffect(() => {
    if (config.BRAND !== 'TIC') {
      history.push('/');
    }
  }, []);

  const unmutePlay = () => {
    const iframe = document.querySelector('iframe');
    const player = new Vimeo.Player(iframe);
    player.setVolume(1);
    player.setCurrentTime(0);
    setPlaying(true);
  };

  const onJoin = async () => {
    try {
      await addProduct(productId);
      await axios
        .get(WEBHOOK, {
          params: {
            account_id: account.account_id,
            member_id: account.member_id,
            account_email: account.account_email,
            user_id: user.user_id,
            user_email: user.user_username,
          },
        })
        .catch((e) => console.log(e));
      window.location = `https://go.textinchurch.com/webelieve_share`;
    } catch (e) {
      snack.error(e.message);
    }
  };

  const hasClaimed = account.subscriptions.find(sub => {
    return sub.items.data.find(i => i.price.id ===productId)
  });

  return (
    <Box bgcolor="background.paper" pt={4} pb={4} className={classes.root}>
      <Helmet title="We Believe" />
      <Container>
        <Box mb={3}>
          <Typography
            variant="h1"
            sx={{ fontWeight: 300, fontSize: 60, lineHeight: 1, mb: 4 }}
            color="primary"
          >
            For the first time in a century, less than 50% of Americans belong
            to a faith community.{' '}
            <em style={{ fontWeight: 500 }}>We believe...</em>
          </Typography>
          <Typography paragraph variant="h2" lineHeight={1.2}>
            You have the{' '}
            <span style={{ fontWeight: 500 }}>power and influence</span> to help
            the local church. We believe if you share Text In Church with 3
            other churches, you will make a{' '}
            <span style={{ fontWeight: 500 }}>big impact</span> in faith
            communities and the Kingdom of God.
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item md={7}>
            <Box className={classes.videoWrapper}>
              {!playing && (
                <Box className={classes.overlay} onClick={unmutePlay}>
                  <div className="unmute-button">
                    <img
                      className="img-fluid"
                      src="/assets/images/sound-on3.png"
                    />
                  </div>
                </Box>
              )}
              <Box className="embed-responsive embed-responsive-16by9">
                <iframe
                  allow="autoplay"
                  src="https://player.vimeo.com/video/569114426?title=0&amp;byline=0&amp;wmode=transparent&amp;autopause=0&amp;autoplay=1&amp;muted=1"
                  frameBorder="0"
                  allowFullScreen=""
                  wmode="opaque"
                  id="fitvid363578"
                />
              </Box>
            </Box>
          </Grid>
          <Grid item md={5}>
            <Typography
              sx={{ mb: 3 }}
              align="center"
              variant="h2"
              fontWeight={500}
              fontSize={34}
              lineHeight={1}
            >
              HERE'S WHAT YOU'LL GET...
            </Typography>
            <Typography paragraph>
              <Box color="primary.main" component="span" fontWeight={500}>
                1,000 BONUS TEXT MESSAGES
              </Box>{' '}
              will be added to your Text In Church account each month for the
              next three months... that's 3,000 more ways to connect with your
              congregation!
            </Typography>
            <Typography paragraph>
              <Box color="primary.main" component="span" fontWeight={500}>
                FREE PDF RESOURCE
              </Box>{' '}
              "3 Post-Pandemic Connection Strategies that Work for Any Church"
              to help you re-engage your community and get them back to
              in-person services and events
            </Typography>
            <Typography paragraph>
              <Box color="primary.main" component="span" fontWeight={500}>
                MAKE KINGDOM IMPACT
              </Box>
              ... By sharing Text In Church, you will empower other local
              churches to care for the people in their communities in meaningful
              ways
            </Typography>
          </Grid>
        </Grid>
        <Box py={7} textAlign="center">
          {hasClaimed ? (
            <Alert icon={false} severity="success" sx={{ fontSize: 18, justifyContent: 'center' }}>
              Thanks for joining the movement. If you need access to the
              resources you can find them{' '}
              <Link target="_blank" href="https://go.textinchurch.com/webelieve_share">
                here
              </Link>
            </Alert>
          ) : (
            <Button
              color="cta"
              sx={{ flexDirection: 'column', pt: 1.5 }}
              onClick={onJoin}
            >
              <Typography
                gutterBottom
                variant="h2"
                fontWeight={600}
                sx={{ color: 'white' }}
              >
                Join The We Believe Movement
              </Typography>
              <Typography sx={{ color: 'white', opacity: 0.7 }}>
                I PLEDGE TO SHARE TEXT IN CHURCH WITH 3 OTHER CHURCHES
              </Typography>
            </Button>
          )}
        </Box>
        <Box textAlign="center" mb={5}>
          <Typography
            sx={{ mb: 4 }}
            variant="h1"
            color="primary"
            fontSize={60}
            fontWeight={600}
          >
            We Believe in Local Churches
          </Typography>
          <Typography variant="h2" fontSize={36}>
            Make your mark in 3 easy steps!
          </Typography>
        </Box>
        <Box>
          <ul className={classes.list}>
            <li>
              <Typography fontSize="inherit">
                <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                <span style={{ fontWeight: 600 }}>
                  Click the orange button
                </span>{' '}
                to Join the We Believe Movement with one click
              </Typography>
            </li>
            <li>
              <Typography fontSize="inherit">
                <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                <span style={{ fontWeight: 600 }}>
                  Share Text In Church with 3 churches
                </span>{' '}
                using the link we provide you
              </Typography>
            </li>
            <li>
              <Typography fontSize="inherit">
                <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                <span style={{ fontWeight: 600 }}>
                  Re-Engage Your Community
                </span>{' '}
                with more text messages and a new invitation strategy
              </Typography>
            </li>
          </ul>
        </Box>
      </Container>
    </Box>
  );
};

export default WeBelieve;
